import React, {useState} from 'react'
import {Box, useMultiStyleConfig} from '@chakra-ui/react'
import {Swiper, SwiperSlide} from 'swiper/react'
import {WallMixedImage} from '../image'
import {WallMixedVideo} from '../video'
import {WallMixedComponent} from '../../index'
import {PAGE_DESIGNER_MODULES, stableKeyFromComponent} from '_utils'

export interface WallMixedCarouselProps {
    components: WallMixedComponent[]
    layout_text_below_image: boolean
    positionStart: number
    analyticsID?: string
    analyticsList?: string
    listingLayout?: string
    analyticsName?: string
    adaptiveHeight?: boolean
}

const WallMixedCarousel: React.FC<WallMixedCarouselProps> = ({
    components,
    layout_text_below_image,
    positionStart,
    analyticsID,
    analyticsList,
    listingLayout,
    analyticsName,
    adaptiveHeight,
}) => {
    const [activeIndex, setActiveIndex] = useState(0)
    const styles = useMultiStyleConfig('WallMixedCarousel', {}) as Record<string, any>

    return (
        <Box {...styles.wrapper} sx={{'& .swiper': {overflow: 'visible'}}} data-testid="wall-mixed-carousel">
            <Swiper
                className="cc-slider cc-content-wall-mixed-slider"
                slidesPerView={1.1}
                spaceBetween={8}
                centeredSlides={true}
                updateOnWindowResize={true}
                loop={false}
                onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
            >
                {components.map((component, colIdx) => {
                    const productPosition = positionStart + colIdx
                    const slideKey = component.id || stableKeyFromComponent(component) || `slide-${colIdx}`
                    const isActiveSlide = colIdx === activeIndex

                    return (
                        <SwiperSlide key={slideKey} className="cc-pd-wall-mixed-item">
                            {component.typeId?.indexOf(PAGE_DESIGNER_MODULES.WALL_MIXED_IMAGE) !== -1 && (
                                <WallMixedImage
                                    isActiveSlide={isActiveSlide}
                                    cld_image_desktop={component.data?.cld_image_desktop}
                                    cld_image_mobile={component.data?.cld_image_mobile}
                                    cld_image_tablet={component.data?.cld_image_tablet}
                                    title={component.data?.title}
                                    text={component.data?.text}
                                    text_align={component.data?.content_t_align}
                                    h_align={component.data?.content_h_align}
                                    v_align={component.data?.content_v_align}
                                    ctaText={component.data?.button_text}
                                    ctaStyle={component.data?.button_style}
                                    animation_enabled={component.data?.animation_enabled}
                                    animation_cta_text={component.data?.animation_cta_text}
                                    animation_text={component.data?.animation_text}
                                    animation_duration={component.data?.animation_duration}
                                    image_padding={component.data?.image_padding}
                                    layout_text_below_image={layout_text_below_image}
                                    button_background={component.data?.button_background}
                                    content_color={component.data?.content_color}
                                    button_color={component.data?.button_color}
                                    productID={component.data?.product}
                                    position={productPosition}
                                    analyticsID={analyticsID}
                                    image_link={component.data?.image_link}
                                    analyticsList={analyticsList}
                                    listingLayout={listingLayout}
                                    mobileSideBySide={false}
                                    analyticsName={analyticsName}
                                    adaptiveHeight={adaptiveHeight}
                                />
                            )}
                            {component.typeId?.indexOf(PAGE_DESIGNER_MODULES.WALL_MIXED_VIDEO) !== -1 && (
                                <WallMixedVideo
                                    isActiveSlide={isActiveSlide}
                                    iframe_id={component.data?.iframe_id}
                                    iframem_id={component.data?.iframem_id}
                                    title={component.data?.title}
                                    layout_text_below_image={layout_text_below_image}
                                    position={productPosition}
                                    video_padding={component.data?.video_padding}
                                    text_align={component.data?.content_t_align}
                                    h_align={component.data?.content_h_align}
                                    v_align={component.data?.content_v_align}
                                    ctaText={component.data?.button_text}
                                    text={component.data?.text}
                                    ctaStyle={component.data?.button_style}
                                    button_background={component.data?.button_background}
                                    content_color={component.data?.content_color}
                                    button_color={component.data?.button_color}
                                    productID={component.data?.product}
                                    analyticsID={analyticsID}
                                    video_link={component.data?.video_link}
                                    analyticsList={analyticsList}
                                    listingLayout={listingLayout}
                                    animation_enabled={component.data?.animation_enabled}
                                    animation_cta_text={component.data?.animation_cta_text}
                                    animation_text={component.data?.animation_text}
                                    mobileSideBySide={false}
                                    analyticsName={analyticsName}
                                    adaptiveHeight={adaptiveHeight}
                                />
                            )}
                        </SwiperSlide>
                    )
                })}
            </Swiper>
        </Box>
    )
}

const MemoizedWallMixedCarousel = React.memo(WallMixedCarousel)

export {MemoizedWallMixedCarousel as WallMixedCarousel}
